import cx from 'classnames';
import React, { useContext, useEffect, useState } from 'react';
import SyntaxHighlighter from 'react-syntax-highlighter/dist/cjs/prism-light';
import bash from 'react-syntax-highlighter/dist/cjs/languages/prism/bash';
import go from 'react-syntax-highlighter/dist/cjs/languages/prism/go';
import groovy from 'react-syntax-highlighter/dist/cjs/languages/prism/groovy';
import http from 'react-syntax-highlighter/dist/cjs/languages/prism/http';
import java from 'react-syntax-highlighter/dist/cjs/languages/prism/java';
import javascript from 'react-syntax-highlighter/dist/cjs/languages/prism/javascript';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import kotlin from 'react-syntax-highlighter/dist/cjs/languages/prism/kotlin';
import markdown from 'react-syntax-highlighter/dist/cjs/languages/prism/markdown';
import markup from 'react-syntax-highlighter/dist/cjs/languages/prism/markup';
import objectivec from 'react-syntax-highlighter/dist/cjs/languages/prism/objectivec';
import python from 'react-syntax-highlighter/dist/cjs/languages/prism/python';
import ruby from 'react-syntax-highlighter/dist/cjs/languages/prism/ruby';
import swift from 'react-syntax-highlighter/dist/cjs/languages/prism/swift';
import tsx from 'react-syntax-highlighter/dist/cjs/languages/prism/tsx';
import {
  coldarkCold,
  coldarkDark,
} from 'react-syntax-highlighter/dist/cjs/styles/prism';

import Context from '../../../contexts/docs';
import { useFernSdkEnabled } from 'src/contexts/docs/useFernSdkEnabled';
import { buildLlmVariants } from './llmVariants';
import {
  ANDROID_LANGUAGE_OPTIONS,
  FRONTEND_LANGUAGE_OPTIONS,
  IOS_LANGUAGE_OPTIONS,
  LANGUAGE_OPTIONS,
  REACT_NATIVE_IOS_LANGUAGE_OPTIONS,
} from '../constants/languages';
import {
  AndroidCodeLanguage,
  CodeBlockType,
  FrontendCodeLanguage,
  IOSCodeLanguage,
  ReactNativeiOSCodeLanguage,
  ServerCodeLanguage,
} from '../types';
import { CheckIcon, CopyIcon, MoonIcon, SunIcon } from './icons';
import { GoSdkToggle } from './GoSdkToggle';

import styles from './index.module.scss';

// Switching from the default `Prism` build (every language) to `prism-light`
// (no languages) and registering only the langs that actually appear in docs
// MDX. Measured savings: ~180 KiB gzipped (~490 KiB raw) on the docs bundle.
//   - `tsx` transitively registers `jsx` + `typescript` + `javascript`.
//   - `markup` is aliased to `html` and `xml` below; refractor doesn't process
//     the grammar's `aliases` array on register, so the alias call is required.
//   - `node` is remapped to `javascript` at render time, see currentLang below.
//
// To add a language: import the grammar module from the same path and add a
// registerLanguage call below. e.g. for Rust:
//   import rust from 'react-syntax-highlighter/dist/cjs/languages/prism/rust';
//   SyntaxHighlighter.registerLanguage('rust', rust);
// If a code fence uses an unregistered language the block renders as plain
// text (no error, no colors).
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('groovy', groovy);
SyntaxHighlighter.registerLanguage('http', http);
SyntaxHighlighter.registerLanguage('java', java);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('kotlin', kotlin);
SyntaxHighlighter.registerLanguage('markdown', markdown);
SyntaxHighlighter.registerLanguage('markup', markup);
SyntaxHighlighter.registerLanguage('objectivec', objectivec);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('ruby', ruby);
SyntaxHighlighter.registerLanguage('swift', swift);
SyntaxHighlighter.registerLanguage('tsx', tsx);
SyntaxHighlighter.alias('markup', ['html', 'xml']);

export type CodeBlockTheme = 'light' | 'dark';

type LanguageVariant =
  | ServerCodeLanguage
  | IOSCodeLanguage
  | AndroidCodeLanguage
  | FrontendCodeLanguage
  | ReactNativeiOSCodeLanguage;

interface SingleCodeProps {
  code: string;
  lang: string;
  variants?: never;
  type?: never;
}

interface MultiCodeProps {
  code?: never;
  lang?: never;
  variants: Partial<Record<LanguageVariant, string>>;
  type: CodeBlockType;
  fernGoCode?: string;
  fernGoUnavailable?: string;
}

type CodeProps = SingleCodeProps | MultiCodeProps;

interface BaseProps {
  className?: Parameters<typeof cx>[0];
  title?: string;
  showLineNumbers?: boolean;
  disableCopy?: boolean;
  theme?: CodeBlockTheme;
  showLineStart?: number;
  showLineEnd?: number;
  maxLines?: number;
  onLineClick?: (lineNumber: number) => void;
  validLineNumbers?: Set<number>;
}

type Props = BaseProps & CodeProps;

const clientRegex = /<Client(?:QD?)?>(.*?)<\/Client(?:QD?)?>/g;
const secretRegex = /<Secret(?:QD?)?>(.*?)<\/Secret(?:QD?)?>/g;

const CodeBlockV2: React.FC<Props> = (props) => {
  const {
    className,
    title,
    showLineNumbers = false,
    disableCopy = false,
    theme: themeOverride,
    showLineStart,
    showLineEnd,
    maxLines,
    onLineClick,
    validLineNumbers,
  } = props;

  const {
    codeBlockTheme,
    goSdkGeneration,
    serverCodeLanguage,
    iOSLanguage,
    androidLanguage,
    frontendCodeLanguage,
    reactNativeiOSLanguage,
    dispatch,
    isLoggedIn,
    user,
  } = useContext(Context);
  const theme = themeOverride || codeBlockTheme;

  const handleThemeToggle = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    dispatch({
      type: 'SET_CODE_BLOCK_THEME',
      payload: { codeBlockTheme: newTheme },
    });
  };

  // Determine if we're in single or multi-language mode
  const isSingleLanguage = 'code' in props && props.code !== undefined;
  const isMultiLanguage = 'variants' in props && props.variants !== undefined;

  // Get language options based on type
  const getLanguageOptions = (type: CodeBlockType) => {
    switch (type) {
      case 'ios':
        return IOS_LANGUAGE_OPTIONS;
      case 'android':
        return ANDROID_LANGUAGE_OPTIONS;
      case 'react_native_ios':
        return REACT_NATIVE_IOS_LANGUAGE_OPTIONS;
      case 'frontend':
        return FRONTEND_LANGUAGE_OPTIONS;
      case 'server':
      default:
        return LANGUAGE_OPTIONS;
    }
  };

  // Get stored language preference based on type
  const getStoredLanguage = (type: CodeBlockType): LanguageVariant => {
    switch (type) {
      case 'ios':
        return iOSLanguage;
      case 'android':
        return androidLanguage;
      case 'react_native_ios':
        return reactNativeiOSLanguage;
      case 'frontend':
        return frontendCodeLanguage;
      case 'server':
      default:
        return serverCodeLanguage as LanguageVariant;
    }
  };

  // State for selected language (only used in multi-language mode)
  const languageOptions = isMultiLanguage ? getLanguageOptions(props.type) : [];
  const storedLanguage = isMultiLanguage ? getStoredLanguage(props.type) : null;
  const defaultLanguage = isMultiLanguage
    ? storedLanguage && props.variants[storedLanguage]
      ? storedLanguage
      : (Object.keys(props.variants)[0] as LanguageVariant)
    : null;
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageVariant>(
    defaultLanguage!,
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const codeContentRef = React.useRef<HTMLDivElement>(null);

  // Replace Client/Secret tags with actual values or remove them
  const imputeClientAndSecretKeys = (code: string) => {
    let newCode = code;
    if (user.showAPIKeys && isLoggedIn && user.team && user.sandboxSecret) {
      newCode = newCode.replace(clientRegex, (match) => {
        if (match.includes('ClientQD')) {
          return `"${user.team['_clientID']}"`;
        } else if (match.includes('ClientQ')) {
          return `'${user.team['_clientID']}'`;
        }
        return user.team['_clientID'];
      });
      newCode = newCode.replace(secretRegex, (match) => {
        if (match.includes('SecretQD')) {
          return `"${user.sandboxSecret}"`;
        } else if (match.includes('SecretQ')) {
          return `'${user.sandboxSecret}'`;
        }
        return user.sandboxSecret;
      });
    } else {
      // Hide <Client> </Client>, <ClientQ>, </ClientQD>, etc.
      newCode = newCode.replace(/<(?:\/)?Client(?:QD?)?>/g, '');
      newCode = newCode.replace(/<(?:\/)?Secret(?:QD?)?>/g, '');
    }
    return newCode;
  };

  // Get current code and language
  const fernSdkEnabled = useFernSdkEnabled();
  const hasGoSdkVariants =
    fernSdkEnabled &&
    isMultiLanguage &&
    props.type === 'server' &&
    selectedLanguage === 'go' &&
    props.variants.go !== undefined &&
    (props.fernGoCode !== undefined || props.fernGoUnavailable !== undefined);
  const isFernGo = hasGoSdkVariants && goSdkGeneration === 'fern';
  const unavailableMessage = isFernGo ? props.fernGoUnavailable : undefined;
  const rawCode = isSingleLanguage
    ? props.code
    : isFernGo
    ? props.fernGoCode || ''
    : props.variants[selectedLanguage] || '';
  const currentCode = imputeClientAndSecretKeys(rawCode);
  const currentLang = isSingleLanguage ? props.lang : selectedLanguage;

  const [isCopying, setIsCopying] = useState(false);

  useEffect(() => {
    if (isCopying) {
      const timeout = setTimeout(() => setIsCopying(false), 3000);
      return () => clearTimeout(timeout);
    }
  }, [isCopying]);

  // Sync selected language with stored preference when it changes
  useEffect(() => {
    if (isMultiLanguage && storedLanguage && props.variants[storedLanguage]) {
      setSelectedLanguage(storedLanguage);
    }
  }, [storedLanguage, isMultiLanguage, props.variants]);

  // Smooth scroll to highlighted line when showLineStart changes
  useEffect(() => {
    if (showLineStart != null && codeContentRef.current) {
      // Wait for next frame to ensure line is rendered
      requestAnimationFrame(() => {
        const preElement = codeContentRef.current?.querySelector('pre');
        if (!preElement) return;

        // Find all line elements (each line is a span with display: block)
        const lines = preElement.querySelectorAll('code > span');
        const targetLine = lines[showLineStart];

        if (targetLine) {
          const container = preElement;
          const lineTop = (targetLine as HTMLElement).offsetTop;
          const containerHeight = container.clientHeight;

          // Scroll so line appears 1/3 from top
          const scrollTarget = lineTop - containerHeight / 3;

          container.scrollTo({
            top: Math.max(0, scrollTarget),
            behavior: 'smooth',
          });
        }
      });
    }
  }, [showLineStart]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(currentCode);
      setIsCopying(true);
    } catch {
      // Ignore — matches the try/catch pattern used by the other clipboard
      // call sites in the docs (MarkdownButton, ChatActions). If the write
      // fails we just don't show the "Copied!" confirmation.
    }
  };

  const handleLanguageChange = (language: LanguageVariant) => {
    setSelectedLanguage(language);
    setIsDropdownOpen(false);

    // Dispatch appropriate action based on type
    if (isMultiLanguage) {
      switch (props.type) {
        case 'ios':
          dispatch({
            type: 'SET_IOS_LANGUAGE',
            payload: { iOSLanguage: language as IOSCodeLanguage },
          });
          break;
        case 'android':
          dispatch({
            type: 'SET_ANDROID_LANGUAGE',
            payload: { androidLanguage: language as AndroidCodeLanguage },
          });
          break;
        case 'react_native_ios':
          // React Native iOS uses the same state as regular iOS
          dispatch({
            type: 'SET_IOS_LANGUAGE',
            payload: { iOSLanguage: language as IOSCodeLanguage },
          });
          break;
        case 'frontend':
          dispatch({
            type: 'SET_FRONTEND_LANGUAGE',
            payload: { frontendCodeLanguage: language as FrontendCodeLanguage },
          });
          break;
        case 'server':
          dispatch({
            type: 'SET_CODE_LANGUAGE',
            payload: { serverCodeLanguage: language as ServerCodeLanguage },
          });
          break;
      }
    }
  };

  // const syntaxTheme =
  //   theme === 'light' ? oneLight : currentLang === 'json' ? darcula : dracula;

  const syntaxTheme = theme === 'light' ? coldarkCold : coldarkDark;

  const isDark = theme === 'dark';

  // Determine which lines should be highlighted
  const shouldHighlightLine = (lineNumber: number): boolean => {
    // Existing line coordinates refer to the legacy snippet.
    if (isFernGo) return false;
    if (showLineStart == null && showLineEnd == null) {
      return false;
    }
    const start = showLineStart || 1;
    const end = showLineEnd || Infinity;
    return lineNumber >= start && lineNumber <= end;
  };

  const selectedLanguageOption = languageOptions.find(
    (opt) => opt.value === selectedLanguage,
  );

  // Strip auth tag wrappers for LLM consumption (mirrors unauthenticated behavior)
  const llmVariants = isMultiLanguage
    ? JSON.stringify(
        buildLlmVariants(
          props.variants,
          props.type === 'server'
            ? { code: props.fernGoCode, unavailable: props.fernGoUnavailable }
            : {},
          process.env.DOCS_FERN_SDK_EXPORT === 'true',
        ),
      )
    : undefined;

  return (
    <div
      className={cx(styles.codeBlockV2, className, {
        [styles.dark]: isDark,
        [styles.light]: !isDark,
      })}
      data-code-variants={llmVariants}
      data-sdk-generation={hasGoSdkVariants ? goSdkGeneration : undefined}
    >
      {((title && title !== ' ') ||
        isMultiLanguage ||
        !disableCopy ||
        !themeOverride) && (
        <div className={styles.header}>
          <span className={styles.title}>{title}</span>
          <div className={styles.headerControls}>
            {hasGoSdkVariants && <GoSdkToggle dark={isDark} />}
            {isMultiLanguage && (
              <div className={styles.languageSwitcher}>
                <button
                  className={styles.languageButton}
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  aria-label='Select language'
                >
                  {selectedLanguageOption?.label}
                  <span className={styles.chevron}>▼</span>
                </button>
                {isDropdownOpen && (
                  <div className={styles.dropdown}>
                    {languageOptions.map((option) => (
                      <button
                        key={option.value}
                        className={cx(styles.dropdownItem, {
                          [styles.active]: option.value === selectedLanguage,
                        })}
                        onClick={() =>
                          handleLanguageChange(option.value as LanguageVariant)
                        }
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {!themeOverride && (
              <button
                className={styles.headerThemeButton}
                onClick={handleThemeToggle}
                onMouseLeave={(e) => e.currentTarget.blur()}
                aria-label={`Switch to ${
                  theme === 'dark' ? 'light' : 'dark'
                } theme`}
                title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
              >
                {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              </button>
            )}
            {!disableCopy && !unavailableMessage && (
              <button
                className={styles.headerCopyButton}
                onClick={handleCopy}
                onMouseLeave={(e) => e.currentTarget.blur()}
                aria-label='Copy code'
                title={isCopying ? 'Copied!' : 'Copy code'}
              >
                {isCopying ? <CheckIcon /> : <CopyIcon />}
              </button>
            )}
          </div>
        </div>
      )}
      {unavailableMessage ? (
        <p role='status' className={styles.sdkNotice}>
          {unavailableMessage} Choose Legacy SDK to view the existing example.
        </p>
      ) : (
        <div className={styles.codeContent} ref={codeContentRef}>
          <SyntaxHighlighter
            language={currentLang === 'node' ? 'javascript' : currentLang}
            style={syntaxTheme}
            showLineNumbers={
              showLineNumbers ||
              shouldHighlightLine(1) ||
              (!isFernGo && validLineNumbers && validLineNumbers.size > 0)
            }
            wrapLines={true}
            wrapLongLines={true}
            lineNumberStyle={(_lineNumber) => {
              if (!showLineNumbers) {
                return { display: 'none' };
              }
              return {};
            }}
            lineProps={(lineNumber) => {
              const style: React.CSSProperties = { display: 'block' };
              const isClickable =
                !isFernGo && validLineNumbers?.has(lineNumber);

              if (shouldHighlightLine(lineNumber)) {
                style.backgroundColor = isDark
                  ? 'rgba(255, 255, 100, 0.1)'
                  : 'rgba(255, 255, 0, 0.2)';
                style.margin = '0 -1em';
                style.padding = '0 1em';
              }

              if (isClickable) {
                style.cursor = 'pointer';
              }

              return {
                style,
                className: isClickable ? 'clickable-line' : undefined,
                onClick:
                  isClickable && onLineClick
                    ? () => onLineClick(lineNumber)
                    : undefined,
              };
            }}
            customStyle={{
              margin: 0,
              borderRadius: title ? '0 0 4px 4px' : '4px',
              ...(maxLines
                ? {
                    maxHeight: `${maxLines * 1.5}em`,
                    overflow: 'auto',
                  }
                : {}),
            }}
          >
            {currentCode}
          </SyntaxHighlighter>
        </div>
      )}
    </div>
  );
};

CodeBlockV2.displayName = 'CodeBlockV2';

export default CodeBlockV2;
